/**
 * Types for the Customer Insights panel's Purchase History section.
 *
 * Every string field is DISPLAY-READY: currency carries its symbol and separators, rates carry `%`,
 * dates arrive already formatted, and an unknown value arrives as the em-dash placeholder. The panel
 * renders these verbatim and never reformats them — see specs/001-customer-insights-slideout.
 */

/** The em-dash placeholder shown for any value the source reports as unknown. U+2014, not a hyphen. */
export const PURCHASE_HISTORY_PLACEHOLDER = '—';

/**
 * How many prior purchases the section shows. Sent on the request so the service returns only these,
 * and enforced again in the selector so a push carrying more cannot render more.
 */
export const PURCHASE_HISTORY_MAX_RECORDS = 5;

/**
 * The two sides of the buy/sell rate row, already display-ready.
 *
 * The buy side is always the DMS buy rate. The sell side is sourced BY DEAL TYPE before it gets
 * here — a Finance deal's APR, a Lease's money factor (`MONEY_FACTOR AS sell_rate_lease` in the
 * design's query) — so the card renders one row and does not need to know which it holds. A lease's
 * APR is ignored even when the warehouse populates it.
 *
 * Either side may be the em-dash placeholder; a lease's money factor is on file for only a small
 * fraction of leases, so its sell side is a dash far more often than not.
 */
export type PurchaseHistoryRate = {
    buyRate: string;
    sellRate: string;
};

export type PurchaseHistoryCard = {
    /** Display-ready and parseable as a date when known; doubles as the ordering key. */
    soldDate: string;
    /** Pre-composed 'YYYY Make Model Trim'; the trim segment and its separator are dropped upstream. */
    vehicle: string;
    dealType: string;
    paymentAmount: string;
    /**
     * The term ALONE, e.g. '60'. Carries no parenthetical — the panel composes the remaining-payment
     * text onto it from `remainingPayments` (see composeTermDisplay).
     */
    termMonths: string;
    /**
     * How many payments are left. The one non-string field, because it is a count rather than display
     * text.
     *
     * `0` and `null` mean different things and neither may be collapsed into the other: `0` is a claim
     * that the loan is **paid off**, `null` is "not known". The source omits this for roughly a third of
     * records, so `null` is common and must not be defaulted to `0`.
     */
    remainingPayments: number | null;
    frontGross: string;
    backGross: string;
    /** `null` when no rate row applies: a Cash deal, or a deal type the source did not report. */
    rate: PurchaseHistoryRate | null;
    /** VPP chip labels. Empty array means no chip area is rendered. */
    vpps: string[];
};

export type PurchaseHistoryStatus = 'loading' | 'ready' | 'empty' | 'unavailable';

/**
 * A discriminated union rather than a set of booleans, so that "exactly one state at a time" is a
 * compile error to violate: `records` cannot exist alongside `loading`, and a count cannot be shown
 * for an empty or unavailable section.
 *
 * `isTakingLonger` sits on `loading` alone, so it cannot be read in a resolved state.
 */
export type PurchaseHistorySectionState =
    | { status: 'loading'; isTakingLonger: boolean }
    | { status: 'ready'; records: PurchaseHistoryCard[] }
    | { status: 'empty' }
    | { status: 'unavailable' };

/* Source form. These names mirror the publishing service's own types VERBATIM
 * (`dr-services-deal-pulse` `purchaseHistoryWorker/types.ts`) — a cross-repo contract, do not tidy
 * them. The service sends raw numbers and a split year/make/model/trim; the client formats. */

/** Deal type as the warehouse reports it. `null` means not on file — never a sentinel string. */
export type PurchaseHistoryDealType = 'Finance' | 'Lease' | 'Cash';

/** GAP and Extended Warranty only, as prices. Null or zero means the product is not shown. */
export type PurchaseHistoryVppServiceData = {
    gap: number | null;
    warranty: number | null;
};

/**
 * One prior purchase, as reported. Unknown is always an explicit `null` — never `0`, `''`, or an
 * absent key. There is deliberately no `vin` field.
 */
export type PurchaseHistoryRecordServiceData = {
    /** ISO `YYYY-MM-DD`; `null` if absent, unparseable, pre-1980 or future-dated. */
    soldDate: string | null;
    year: number | null;
    make: string | null;
    model: string | null;
    trim: string | null;
    dealType: PurchaseHistoryDealType | null;
    /** A raw number — the consumer formats it. */
    monthlyPayment: number | null;
    termMonths: number | null;
    /** Floored at 0. `0` means paid off, `null` means not derivable — never collapse the two. */
    remainingPayments: number | null;
    frontGross: number | null;
    /** A single figure — the service does not split back gross. */
    backGross: number | null;
    /** Every financed deal type — the rate row's buy side, on a Lease as well as a Finance deal. */
    buyRate: number | null;
    /** Finance only — the APR, which is a Finance deal's sell side. Ignored on a Lease. */
    sellRate: number | null;
    /** Lease only — a Lease's sell side, in place of the APR. On file for a minority of leases. */
    moneyFactor: number | null;
    /** Finance only. Received but not displayed: it is not in the Solution Design's AC-2 field list. */
    financeReserve: number | null;
    vpp: PurchaseHistoryVppServiceData;
};

/** Note `'error'`, not `'failed'`. The acknowledgement uses `'retrieving'`, never seen on a push. */
export type PurchaseHistoryPushState = 'ready' | 'empty' | 'error';

/** Bounded codes, present iff `state === 'error'`. Never free-form text. */
export type PurchaseHistoryFailureReason = 'config-missing' | 'snowflake-error' | 'timeout' | 'unknown';

/** The live push — the only carrier of records; the acknowledgement never has any. */
export type PurchaseHistoryCompletionServiceData = {
    /** `ready` = at least one record; `empty` = zero from a success; `error` = failed or abandoned. */
    state: PurchaseHistoryPushState;
    /** Mirrors the broadcast filter. This is the deal-exchange id, and is the fan-out defence key. */
    dealId: string;
    /** `records.length`; `0` for both empty and error. */
    count: number;
    /** `[]` for empty and error — never partial. */
    records: PurchaseHistoryRecordServiceData[];
    /** Received but NOT displayed — the Figma design has no such line. Typed because it is sent. */
    dataAsOfNote: string;
    /** Present iff `state === 'error'`. */
    failureReason?: PurchaseHistoryFailureReason;
    /** Correlation only; nothing routes on it, and the service publishes without one. */
    requestId?: string;
};

/** Holds no customer name, value or identifier — by construction there is nowhere to put one. */
export type PurchaseHistoryNotificationState = {
    isRaised: boolean;
    /**
     * Same shape as `UniqueScrollRequestId` in `dr-dash-ui-utils`, restated rather than imported:
     * that package imports this one, so importing back would be a cycle.
     */
    uniqueScrollRequestId: string | boolean;
};

/**
 * The `purchaseHistory` slice. `records` holds the SOURCE form as received; conversion to display
 * form is the selector's job, so formatting never leaks into state.
 */
export type PurchaseHistoryState = {
    status: PurchaseHistoryStatus;
    /** Empty unless ready. Replaced wholesale on each push, never appended to. */
    records: PurchaseHistoryRecordServiceData[];
    /** True once the slow-hint interval elapsed with the request still outstanding. */
    isTakingLonger: boolean;
    /** A push is accepted ONLY while this is true. */
    isRequestOutstanding: boolean;
    /** Anchored to the FIRST request; a re-open does not reset it. */
    requestedAtMs: number | null;
    /** Echoed from the acknowledgement, for log correlation only. */
    correlationId: string | null;
    notification: PurchaseHistoryNotificationState;
};
